import { LibraryBig, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { request } from "../api";
import { ErrorCard, Loading } from "../components/AsyncState";
import { BookCard } from "../components/BookCard";
import { UploadBook } from "../components/UploadBook";
import type { Book } from "../types";
import { errorMessage } from "../utils";

export function LibraryPage() {
  const [books, setBooks] = useState<Book[] | null>(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      setBooks(await request<Book[]>("/api/books"));
      setError("");
    } catch (reason) {
      setError(errorMessage(reason));
    }
  }, []);
  useEffect(() => void load(), [load]);
  const filtered = useMemo(
    () =>
      (books ?? []).filter((book) =>
        `${book.title} ${book.author ?? ""}`.toLowerCase().includes(query.toLowerCase())
      ),
    [books, query]
  );

  if (error) return <ErrorCard message={error} retry={() => void load()} />;
  if (!books) return <Loading label="正在打开书库" />;

  return (
    <div className="stack">
      <header className="page-heading">
        <div>
          <span className="eyebrow"><LibraryBig size={14} /> Bibliothèque</span>
          <h1>你的法语书架</h1>
          <p>阅读无需等待分析；分析完成后，预习和词汇统计会自动出现。</p>
        </div>
        <UploadBook onUploaded={(book) => setBooks((items) => [book, ...(items ?? [])])} />
      </header>
      <label className="search-field">
        <Search size={18} />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索书名或作者" />
      </label>
      {filtered.length ? (
        <div className="book-grid book-grid--wide">{filtered.map((book) => <BookCard key={book.id} book={book} />)}</div>
      ) : (
        <div className="empty-state">
          <LibraryBig size={34} />
          <h3>{query ? "没有匹配的书" : "书架还是空的"}</h3>
          <p>{query ? "换一个关键词试试。" : "导入第一本 EPUB，建立你的阅读历程。"}</p>
        </div>
      )}
    </div>
  );
}
